import { Backdrop, Box, Dialog, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerDetails from "./CustomerDetails";
import PaymentDetails from "./PaymentDetails/PaymentDetails";
import ReservationSection from "./ReservationSection";
import StayDetails from "./StayDetails";
import { GenericError, withMultipleQueryData } from "../../../../../common/withQueryData";
import { OrdinaryButton } from "../../../../../common/Buttons";
import { RESERVATION_STATUS } from "../../../../../common/enums";
import { CustomBackdrop } from "../../../../../common/LoadingScreen";
import ReservationStatus from "../ReservationStatus";
import { useReservationsData } from "../../../../../common/hooks/useReservationsData";
import { useAlertNotification } from "../../../../../common/hooks/useAlertNotification";

const ReservationLoadingError = ({ error, onReload }) => {
    const navigate = useNavigate();

    const onClose = () => navigate('..');

    return <>
        <DialogTitle>
            <IconButton
                aria-label="close"
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
                onClick={onClose}
            >
                <CloseIcon />
            </IconButton>
        </DialogTitle>
        <GenericError error={error} onReload={onReload} />
    </>
}

const ReservationPopup = ({ reservationId, propertyId, reload }) => {
    const [editedSection, setEditedSection] = useState(null);

    const { setAlert } = useAlertNotification();

    const navigate = useNavigate();

    const { queryResults: reservationQueryResults, updateReservation } = useReservationsData(propertyId, reservationId);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const onError = useCallback((error) => {
        setAlert({
            message: error.message,
            severity: 'error'
        });
    }, [setAlert]);

    const onEdit = useCallback((sectionTitle) => {
        setEditedSection(sectionTitle);
    }, []);

    const onClose = () => navigate('..');

    const saveReservation = useCallback(async (updatedReservation, reservationChanged = true, additionalActions = []) => {
        try {
            reservationChanged && await updateReservation(updatedReservation);
            await Promise.all(additionalActions.map(action => action()));

            setAlert({
                severity: 'success',
                message: 'Reservation successfully modified',
            })

            setEditedSection(null);
        }
        catch (error) {
            onError(error);
            throw error;
        }
    }, [updateReservation, onError, setAlert]);

    const cancelEditing = useCallback(() => {
        setEditedSection(null);
    }, []);

    return <Dialog
        open
        fullWidth
        maxWidth={isMobile ? '100%' : '80%'}
        PaperProps={{
            sx: {
                minHeight: '90%',
                width: '100%',
                margin: {
                    xs: '0px',
                    md: '32px',
                }
            }
        }}
    >
        <ReservationDialogContentWrapperWithMultipleQueryData
            queryResults={{
                reservation: reservationQueryResults
            }}
            propertyId={propertyId}
            onEdit={onEdit}
            onEditCancel={cancelEditing}
            onSave={saveReservation}
            onClose={onClose}
            onError={onError}
            editedSection={editedSection}
        />
    </Dialog>
}

const ReservationDialogContent = ({ reservation, propertyId, onEdit, onEditCancel, onSave, onClose, onError, editedSection }) => {
    const [saving, setSaving] = useState(false);

    const cancelReservation = async () => {
        setSaving(true);

        try {
            await onSave({
                ...reservation,
                status: RESERVATION_STATUS.CANCELLED
            });
        } catch (error) {
            onError(error);
        }

        setSaving(false);
    }

    return <>
        {editedSection && <Backdrop open sx={{ position: 'absolute', zIndex: 1 }} />}
        {saving && <CustomBackdrop />}
        <DialogTitle sx={{ display: 'flex', alignItems: 'baseline' }}>
            <Typography>
                {`Confirmation #: ${reservation.confirmationNumber}`}
            </Typography>
            <ReservationStatus status={reservation.status} />
            {reservation.status !== RESERVATION_STATUS.CANCELLED && <OrdinaryButton color='error' text='Cancel reservation' loading={saving} onClick={cancelReservation} />}
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

        </DialogTitle>
        <DialogContent
            sx={{
                display: 'flex',
                alignContent: 'flex-start',
                flexWrap: 'wrap',
                justifyContent: 'flex-start',
                padding: {
                    xs: '0px',
                    md: '0px 24px 20px'
                },
                gap: '30px',
            }}
        >
            <Box sx={{ width: { xs: '100%', md: 'fit-content' }, display: 'flex', flexDirection: 'column', flexGrow: 1, flexBasis: '100px', gap: '10px' }}>
                <ReservationSection
                    sectionId={'StayDetails'}
                    title={'Accommodation & Dates'}
                    editable={reservation.status !== RESERVATION_STATUS.CANCELLED}
                    editing={editedSection === 'StayDetails'}
                    disabled={editedSection && editedSection !== 'StayDetails'}
                    onEdit={onEdit}
                    onEditCancel={onEditCancel}
                    onSave={onSave}
                    onError={onError}
                    InnerComponent={StayDetails}
                    reservation={reservation}
                    innerConponentProps={{
                        propertyId: propertyId
                    }}
                />
                <ReservationSection
                    sectionId={'CustomerDetails'}
                    title={'Customer Details'}
                    editable={reservation.status !== RESERVATION_STATUS.CANCELLED}
                    editing={editedSection === 'CustomerDetails'}
                    disabled={editedSection && editedSection !== 'CustomerDetails'}
                    onEdit={onEdit}
                    onEditCancel={onEditCancel}
                    onSave={onSave}
                    onError={onError}
                    InnerComponent={CustomerDetails}
                    reservation={reservation}
                />
            </Box>
            <Box sx={{ width: { xs: '100%', md: 'fit-content' }, display: 'flex', flexDirection: 'column', flexGrow: 1, flexBasis: '100px', gap: '10px' }}>
                <ReservationSection
                    sectionId={'PaymentDetails'}
                    title={'Payment Details'}
                    editable={reservation.status !== RESERVATION_STATUS.CANCELLED}
                    editing={editedSection === 'PaymentDetails'}
                    disabled={editedSection && editedSection !== 'PaymentDetails'}
                    onEdit={onEdit}
                    onEditCancel={onEditCancel}
                    onSave={onSave}
                    onError={onError}
                    InnerComponent={PaymentDetails}
                    reservation={reservation}
                    innerConponentProps={{
                        propertyId: propertyId
                    }}
                />
            </Box>
        </DialogContent>
    </>
}

const ReservationDialogContentWrapper = ({ queryResults, reloadAll, ...props }) => {
    const { reservation: reservationQueryResult } = queryResults;

    /* eslint-disable no-unused-vars */
    const { data: reservation, refetch: reloadReservation } = reservationQueryResult;
    /* eslint-enable no-unused-vars */

    return <ReservationDialogContent
        reservation={reservation}
        {...props}
    />
}

const ReservationDialogContentWrapperWithMultipleQueryData = withMultipleQueryData(
    ReservationDialogContentWrapper,
    ReservationLoadingError
)

export default ReservationPopup;